// kfe-true-false-exercise.js — True/False testing screen.
//
// PORT TARGET: iEnglish/Classes/KFEModules/Learning/TrueFalseContentTesting/
//              KFETrueFalseTesting{Audio,TextOnly,AudioAndText}VC.{h,m}
// Engine:      ../engines/true-false-engine.js (state machine, no DOM)
//
// One screen, three variants — `TFType` selects which of the phrase
// cards renders as audio-only, text-only, or audio + text. Sequencing
// (auto-play A → reveal B → reveal Yes/No) lives here in the
// presentation layer; the engine only knows index / answer / score.
//
// Audio rule (CLAUDE.md #3): the first play() must run inside a user
// gesture. We chain off the Start button so the first instance's auto-
// play A is allowed; subsequent instances inherit Safari's unlocked
// AudioContext via the "Go on" button (also a user gesture).

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { persistCompletion } from "./completion-persist.js";
import { TrueFalseEngine } from "../engines/true-false-engine.js";

/** @typedef {import("../engines/true-false-engine.js").TFType} TFType */
/** @typedef {import("../engines/true-false-engine.js").TFPair} TFPair */
/** @typedef {import("../engines/true-false-engine.js").TFPhrase} TFPhrase */
/** @typedef {import("../engines/true-false-engine.js").TFState} TFState */

const DEFAULT_INSTANCES = 4;
const AUTO_PLAY_DELAY_MS = 500;

export class KFETrueFalseExercise extends KFEComponent {
  static props = ["itemId"];
  static styles = `
    :host { display: block; max-width: 540px; margin: 0 auto; padding: 0 1rem; box-sizing: border-box; }
    h2 { margin-top: 0; }
    .progress { font-size: 0.85rem; color: var(--kfe-muted);
                text-align: right; margin-bottom: 0.25rem; }
    .empty, .complete { padding: 2rem; text-align: center; color: var(--kfe-muted); }
    button.primary { padding: 0.5rem 1rem; border-radius: 8px;
                     background: var(--kfe-accent); color: var(--kfe-on-accent);
                     border: 0; cursor: pointer; }
    .start-prompt { padding: 2rem; text-align: center; }

    .card { display: flex; align-items: center; gap: 0.75rem;
            margin: 0.75rem 0; padding: 0.875rem 1rem;
            border-radius: 12px; background: var(--kfe-surface);
            cursor: pointer; user-select: none; }
    .card.hidden { visibility: hidden; }
    .card.playing { outline: 2px solid var(--kfe-accent); }
    .card-label { font-size: 0.75rem; color: var(--kfe-muted);
                  text-transform: uppercase; letter-spacing: 0.05em; }
    .card-text { font-size: 1.15rem; color: var(--kfe-fg); }
    .card-body { display: flex; flex-direction: column; gap: 0.15rem; flex: 1; }
    .play-btn { flex: 0 0 auto; width: 2.5rem; height: 2.5rem;
                border-radius: 50%; border: 0;
                background: var(--kfe-accent); color: var(--kfe-on-accent);
                font-size: 1rem; cursor: pointer; }
    .play-btn.playing { animation: pulse 1s infinite; }
    @keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.5 } }

    .question { padding: 1rem; text-align: center; font-size: 1.1rem;
                color: var(--kfe-fg); margin: 1rem 0 0.5rem; }
    .answer-row { display: flex; gap: 0.75rem; justify-content: center; }
    .answer-btn { padding: 0.75rem 1.75rem; border-radius: 999px;
                  font-size: 1.05rem; font-weight: 600;
                  border: 0; cursor: pointer; }
    .answer-btn.yes { background: #2e7d32; color: #fff; }
    .answer-btn.no  { background: #c62828; color: #fff; }

    .feedback-icon { text-align: center; font-size: 4rem; line-height: 1;
                     margin: 1rem 0 0.5rem; }
    .feedback-icon.ok   { color: #4caf50; }
    .feedback-icon.fail { color: #e57373; }
    .feedback-text { text-align: center; font-size: 1.5rem; font-weight: 700;
                     margin-bottom: 1rem; }
    .feedback-actions { display: flex; justify-content: center;
                        margin-top: 1rem; }
  `;

  /** @type {TrueFalseEngine | null} */
  engine = null;

  constructor() {
    super();
    this._loaded = false;
    this._noContent = false;
    this._started = false;
    this._completionPersisted = false;
    // Brief 199: a completion write is in flight (so a second terminal event
    // in this lifecycle does not start another one).
    this._completionPending = false;
    /** @type {TFType} */ this._tfType = "audio_text";
    this._instances = DEFAULT_INSTANCES;
    /** @type {string} */ this._containerItemId = "";

    // Per-instance presentation flags. Reset on every new instance.
    this._bRevealed = false;
    this._qRevealed = false;
    /** @type {"a"|"b"|"both"|null} */ this._playing = null;
    // Monotonic play token. A new play (e.g. tapping B while A is mid-flight)
    // bumps this; the superseded play's `finally` checks it before resetting
    // `_playing`, so it can't clobber the newer phrase's state (Brief 110 #4).
    this._playGen = 0;
    /** @type {ReturnType<typeof setTimeout>|null} */ this._autoPlayTimer = null;
  }

  async onConnected() {
    await Promise.all([
      services.items.loadContentItems(),
      services.tfConfig.loadTfTests(),
    ]);
    const itemIdRaw = /** @type {any} */ (this).itemId;
    const itemIdNum = Number(itemIdRaw);
    /** @type {any} */
    const item = Number.isFinite(itemIdNum) ? services.items.getItem(itemIdNum) : undefined;

    this._containerItemId = item?.id ? String(item.id) : `item_${itemIdRaw}`;

    // Brief 083 (Option A): merge the plist-derived test descriptor onto the
    // catalog item so the existing field reads below pick it up. The catalog
    // entry carries no pairs/TFType/instances/passScore — those live in
    // tf-tests.json (extracted from the 2022 plists). Falls back to whatever
    // the catalog had (empty) when getTfTest returns null.
    const tf = services.tfConfig.getTfTest(itemIdNum);
    const merged = tf ? { ...item, ...tf } : item;

    /** @type {TFPair[]} */
    const pairs = Array.isArray(merged?.pairs) ? merged.pairs : [];
    const instances = Number(merged?.instances);
    const tfType = /** @type {TFType} */ (merged?.TFType ?? "audio_text");

    if (pairs.length === 0) {
      this._noContent = true;
      this._loaded = true;
      this._scheduleRender();
      return;
    }

    this._tfType = tfType;
    this._instances = Number.isFinite(instances) && instances > 0
      ? instances
      : DEFAULT_INSTANCES;

    // Brief 049: pass the item's pass threshold (iOS `mustPercent`) through so
    // the completion screen can render the canonical pass vs fail button sets.
    const passScore = Number(merged?.passScore ?? merged?.mustPercent);
    this.engine = new TrueFalseEngine(
      pairs,
      this._instances,
      this._tfType,
      Number.isFinite(passScore) ? { passScore } : undefined,
    );

    const onState = () => {
      // Brief 060b: persist completion once the engine finishes so this T/F
      // item's words enter the SR Auto met-pool.
      if (this.engine?.snapshot().mode === "complete") this._markCompleted();
      this._scheduleRender();
    };
    const onCombination = (/** @type {Event} */ e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      services.learner.recordSession({
        startedAt: Date.now(),
        endedAt: Date.now(),
        exerciseType: "true_false",
        itemId: itemIdNum,
        summary: {
          type: "tf_combination",
          containerItemId: this._containerItemId,
          ...detail,
        },
      }).catch((err) => console.warn("[tf] recordSession failed", err));
    };
    this.engine.addEventListener("stateChanged", onState);
    this.engine.addEventListener("tf_combination", onCombination);
    this.addCleanup(() => this.engine?.removeEventListener("stateChanged", onState));
    this.addCleanup(() => this.engine?.removeEventListener("tf_combination", onCombination));

    this._loaded = true;
    this._scheduleRender();
  }

  onDisconnected() {
    this._clearAutoPlayTimer();
    services.audio.stop();
  }

  // ─── Handlers ─────────────────────────────────────────────────

  _handleStart() {
    if (!this.engine || this._started) return;
    this._started = true;
    this.engine.start();
    this._resetInstanceFlags();
    this._beginInstancePlayback();
  }

  _handlePlayA() {
    this._clearAutoPlayTimer();
    void this._playA(/*revealBAfter*/ !this._bRevealed);
  }

  _handlePlayB() {
    if (!this._bRevealed) this._bRevealed = true;
    void this._playB();
  }

  /** @param {boolean} userSaidYes */
  _handleAnswer(userSaidYes) {
    if (!this.engine) return;
    const s = this.engine.snapshot();
    if (s.mode !== "instance") return;
    this._clearAutoPlayTimer();
    services.audio.stop();
    this.engine.answer(userSaidYes);
  }

  _handlePlayBoth() {
    if (!this.engine) return;
    const s = this.engine.snapshot();
    if (s.mode !== "feedback" || !s.instance) return;
    void this._playBoth(s.instance.correctPair);
  }

  _handleAdvance() {
    if (!this.engine) return;
    this.engine.advance();
    this._resetInstanceFlags();
    const s = this.engine.snapshot();
    if (s.mode === "instance") this._beginInstancePlayback();
  }

  // ─── Audio sequencing ─────────────────────────────────────────

  /**
   * Start the presentation for a freshly-mounted instance.
   *
   * iOS canon (CLAUDE.md #9): the three TFType variants diverge here.
   *   • audio_only / audio_text — KFETrueFalseTestingAudioAndTextVC.m auto-plays
   *     phrase A (`[self.viewModel playAudio:audioUrl …]`, line 300) and staggers
   *     the reveal of B → question off each clip finishing.
   *   • text_only — KFETrueFalseTestingTextOnlyVC.m has NO playAudio call at all:
   *     `resetUIDidOnNewTest` (lines 207-214) reveals phrase A, phrase B and the
   *     question together and waits for the read-only judgement. The learner is
   *     meant to read silently (Brief 184).
   *
   * The feedback-screen "listen to the correct pair" replay is a separate,
   * user-initiated affordance (iOS keeps the audio URLs on the text_only warning
   * view) and is intentionally NOT gated here — Brief 184 Gotcha #3.
   */
  _beginInstancePlayback() {
    if (this._tfType === "text_only") {
      this._bRevealed = true;
      this._qRevealed = true;
      this._scheduleRender();
      return;
    }
    this._scheduleAutoPlayA();
  }

  _scheduleAutoPlayA() {
    this._clearAutoPlayTimer();
    // Small delay so the new instance's cards render before audio starts.
    // User taps on play-A cancel the timer and run the same path inline.
    this._autoPlayTimer = setTimeout(() => {
      this._autoPlayTimer = null;
      void this._playA(/*revealBAfter*/ true);
    }, AUTO_PLAY_DELAY_MS);
  }

  _clearAutoPlayTimer() {
    if (this._autoPlayTimer) {
      clearTimeout(this._autoPlayTimer);
      this._autoPlayTimer = null;
    }
  }

  /** @param {boolean} revealBAfter */
  async _playA(revealBAfter) {
    if (!this.engine) return;
    const s = this.engine.snapshot();
    const a = s.instance?.a;
    if (!a) return;
    // Don't early-return when something is already playing — tapping a phrase
    // must supersede the in-flight one (Brief 110 #4). services.audio.play()
    // aborts the prior clip's token internally (NOT stop(), which would emit
    // the Global-Playlist resume signal mid-exercise — Brief 091).
    const gen = ++this._playGen;
    this._playing = "a";
    this._scheduleRender();
    try {
      await this._playPhrase(a);
    } catch (err) {
      console.warn("[tf] phrase A play failed", err);
    } finally {
      if (this._playGen === gen) {
        this._playing = null;
        if (revealBAfter && !this._bRevealed) this._bRevealed = true;
        this._scheduleRender();
      }
    }
  }

  async _playB() {
    if (!this.engine) return;
    const s = this.engine.snapshot();
    const b = s.instance?.b;
    if (!b) return;
    const gen = ++this._playGen;
    this._playing = "b";
    this._scheduleRender();
    try {
      await this._playPhrase(b);
    } catch (err) {
      console.warn("[tf] phrase B play failed", err);
    } finally {
      if (this._playGen === gen) {
        this._playing = null;
        if (!this._qRevealed) this._qRevealed = true;
        this._scheduleRender();
      }
    }
  }

  /** @param {TFPair} pair */
  async _playBoth(pair) {
    if (this._playing) return;
    this._playing = "both";
    this._scheduleRender();
    try {
      await this._playPhrase(pair.a);
      await new Promise((r) => setTimeout(r, 300));
      await this._playPhrase(pair.b);
    } catch (err) {
      console.warn("[tf] play-both failed", err);
    } finally {
      this._playing = null;
      this._scheduleRender();
    }
  }

  /** @param {TFPhrase} phrase */
  _playPhrase(phrase) {
    // `target.audioUrl` is the legacy plist filename (e.g. "KFET-F1239Ma1.m4a"),
    // NOT a served URL — Brief 083 stored it verbatim and left resolution to
    // play time (tf-config-loader.js header). Passing it as `url` set it
    // directly as <audio>.src, a relative path that 404s against the current
    // /exercise/<id> route, so playback silently no-op'd (Brief 110). Pass it
    // as `audioFileName` so the engine resolves it through media-paths.js
    // resolveAudio() → /content/Media/Audio/… exactly like L2B/HAAI do.
    const audioFileName = phrase?.target?.audioUrl;
    if (!audioFileName) return Promise.resolve();
    return services.audio.play({ audioFileName });
  }

  _resetInstanceFlags() {
    this._bRevealed = false;
    this._qRevealed = false;
    this._playing = null;
  }

  // Brief 046 completion-screen handlers. TF has no per-MMU streak — the
  // engine is reseeded on Redo, which is enough to walk again from instance
  // 1.
  async _onRedo() {
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (Number.isFinite(itemId)) {
      try {
        await services.learner.clearItemProgress(itemId, []);
      } catch (err) {
        console.warn("[tf] clearItemProgress failed", err);
      }
    }
    this._clearAutoPlayTimer();
    services.audio.stop();
    this._started = false;
    this._loaded = false;
    this._noContent = false;
    this._completionPersisted = false;
    // Brief 199: a completion write is in flight (so a second terminal event
    // in this lifecycle does not start another one).
    this._completionPending = false;
    this._resetInstanceFlags();
    this.engine = null;
    this._scheduleRender();
    this.onConnected();
  }

  _onReturnToLesson() {
    history.back();
  }

  /** Persist item completion once per view (Brief 060b).
  // Brief 199, criterion 3: the latch is set only AFTER the write has resolved,
  // and a failure leaves it clear so a later terminal event can try again. It
  // used to be set before the write was even attempted, so a rejected write was
  // never retried by this view — six of the 2022 walk's nine lost completions.
  // `_completionPending` keeps a second terminal event in the same lifecycle
  // from starting a second write (learner-store shares the in-flight promise
  // too, so neither can double-count — `completedCount` is a counter, Brief 072).
   */
  async _markCompleted() {
    if (this._completionPersisted || this._completionPending) return;
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(itemId)) return;
    this._completionPending = true;
    const ok = await persistCompletion(itemId);
    this._completionPending = false;
    this._completionPersisted = ok;
  }

  // ─── Render ───────────────────────────────────────────────────

  render() {
    if (!this._loaded) return html`<p>${t("loading")}</p>`;
    if (this._noContent) {
      return html`<div class="empty">
        <h2>${t("tf.title")}</h2>
        <p>${t("tf.empty")}</p>
      </div>`;
    }
    if (!this.engine) return html`<p>${t("loading")}</p>`;

    if (!this._started) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "primary";
      btn.textContent = t("lesson.start");
      btn.addEventListener("click", () => this._handleStart());
      return html`
        <h2>${t("tf.title")}</h2>
        <div class="start-prompt">${btn}</div>
      `;
    }

    const s = this.engine.snapshot();

    if (s.mode === "complete") {
      const stats = document.createElement("div");
      stats.className = "complete-stats";
      stats.textContent = t("tf.score", {
        correct: String(s.correctCount),
        total: String(s.total),
      });
      // Brief 049: iOS-canonical True/False summary (KFETFSummaryVC) differs by
      // outcome. PASS → header "Well done" with two stacked buttons "Keep
      // going" (继续学习其他内容, returns to lesson) above "Redo the test"
      // (重做测试, restarts). FAIL → header "Sorry, your score isn't high
      // enough" with a single "Do the test again" (再做一次测试) button.
      const screen = document.createElement("kfe-completion-screen");
      /** @type {any} */ (screen).layout = "column";
      if (s.passed) {
        /** @type {any} */ (screen).message = t("completion.trueFalse.wellDone");
        /** @type {any} */ (screen).actions = [
          { label: t("completion.trueFalse.keepGoing"), eventName: "kfe:trueFalse:keepGoing", primary: true },
          { label: t("completion.trueFalse.redo"), eventName: "kfe:trueFalse:redo" },
        ];
        screen.addEventListener("kfe:trueFalse:keepGoing", () => this._onReturnToLesson());
        screen.addEventListener("kfe:trueFalse:redo", () => this._onRedo());
      } else {
        /** @type {any} */ (screen).message = t("completion.trueFalse.notHighEnough");
        /** @type {any} */ (screen).actions = [
          { label: t("completion.trueFalse.failRetry"), eventName: "kfe:trueFalse:redo", primary: true },
        ];
        screen.addEventListener("kfe:trueFalse:redo", () => this._onRedo());
      }
      screen.appendChild(stats);
      return html`${screen}`;
    }

    if (!s.instance) return html`<p>${t("loading")}</p>`;

    const progress = t("tf.progress", {
      current: String(s.index + 1),
      total: String(s.total),
    });

    if (s.mode === "feedback") return this._renderFeedback(s, progress);
    return this._renderInstance(s, progress);
  }

  /**
   * @param {TFState} s
   * @param {string} progress
   */
  _renderInstance(s, progress) {
    const inst = s.instance;
    if (!inst) return html`<p>${t("loading")}</p>`;

    const cardA = this._buildCard("A", inst.a, "a",
      this._playing === "a",
      () => this._handlePlayA(), /*hidden*/ false);
    const cardB = this._buildCard("B", inst.b, "b",
      this._playing === "b",
      () => this._handlePlayB(), /*hidden*/ !this._bRevealed);

    const showButtons = this._qRevealed;
    const question = document.createElement("div");
    question.className = "question";
    question.textContent = t("tf.question");

    const yesBtn = document.createElement("button");
    yesBtn.type = "button";
    yesBtn.className = "answer-btn yes";
    yesBtn.textContent = t("tf.yes");
    yesBtn.addEventListener("click", () => this._handleAnswer(true));

    const noBtn = document.createElement("button");
    noBtn.type = "button";
    noBtn.className = "answer-btn no";
    noBtn.textContent = t("tf.no");
    noBtn.addEventListener("click", () => this._handleAnswer(false));

    const answerRow = document.createElement("div");
    answerRow.className = "answer-row";
    answerRow.append(yesBtn, noBtn);

    return html`
      <div class="progress">${progress}</div>
      <h2>${t("tf.title")}</h2>
      ${cardA}
      ${cardB}
      ${showButtons ? question : ""}
      ${showButtons ? answerRow : ""}
    `;
  }

  /**
   * @param {TFState} s
   * @param {string} progress
   */
  _renderFeedback(s, progress) {
    const ok = s.wasCorrect;
    const icon = document.createElement("div");
    icon.className = "feedback-icon " + (ok ? "ok" : "fail");
    icon.textContent = ok ? "✓" : "✗";

    const label = document.createElement("div");
    label.className = "feedback-text";
    label.textContent = ok ? t("tf.correct") : t("tf.wrong");

    const inst = s.instance;
    const listenPhrase = ok ? t("tf.listenBoth") : t("tf.listenCorrect");

    const listenCard = document.createElement("div");
    listenCard.className = "card" + (this._playing === "both" ? " playing" : "");
    listenCard.addEventListener("click", () => this._handlePlayBoth());

    const playBtn = document.createElement("button");
    playBtn.type = "button";
    playBtn.className = "play-btn" + (this._playing === "both" ? " playing" : "");
    playBtn.textContent = "▶";
    playBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this._handlePlayBoth();
    });

    const body = document.createElement("div");
    body.className = "card-body";
    const textRow = document.createElement("div");
    textRow.className = "card-text";
    textRow.textContent = listenPhrase;
    body.appendChild(textRow);
    // For text-only / audio_text show the canonical pair text alongside.
    if (this._tfType !== "audio_only" && inst) {
      const pairText = document.createElement("div");
      pairText.className = "card-label";
      const aText = inst.correctPair.a?.target?.text ?? "";
      const bText = inst.correctPair.b?.target?.text ?? "";
      pairText.textContent = `${aText} → ${bText}`;
      body.appendChild(pairText);
    }
    listenCard.append(playBtn, body);

    const goOn = document.createElement("button");
    goOn.type = "button";
    goOn.className = "primary";
    goOn.textContent = t("tf.goOn");
    goOn.addEventListener("click", () => this._handleAdvance());
    const actions = document.createElement("div");
    actions.className = "feedback-actions";
    actions.appendChild(goOn);

    return html`
      <div class="progress">${progress}</div>
      <h2>${t("tf.title")}</h2>
      ${icon}
      ${label}
      ${listenCard}
      ${actions}
    `;
  }

  /**
   * @param {string} label
   * @param {TFPhrase} phrase
   * @param {"a"|"b"} which
   * @param {boolean} playing
   * @param {() => void} onPlay
   * @param {boolean} hidden
   * @returns {HTMLElement}
   */
  _buildCard(label, phrase, which, playing, onPlay, hidden) {
    const card = document.createElement("div");
    card.className = "card" + (playing ? " playing" : "") + (hidden ? " hidden" : "");
    card.dataset.which = which;

    const showAudio = this._tfType === "audio_only" || this._tfType === "audio_text";
    const showText  = this._tfType === "text_only"  || this._tfType === "audio_text";

    // text_only cards are read-only (iOS PhraseLabelTypeTextOnly is not an audio
    // affordance) — only bind the replay tap when this variant plays audio.
    if (!hidden && showAudio) card.addEventListener("click", () => onPlay());

    if (showAudio) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "play-btn" + (playing ? " playing" : "");
      btn.textContent = "▶";
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (!hidden) onPlay();
      });
      card.appendChild(btn);
    }

    const body = document.createElement("div");
    body.className = "card-body";
    const labelEl = document.createElement("div");
    labelEl.className = "card-label";
    labelEl.textContent = t("tf.phraseLabel", { label });
    body.appendChild(labelEl);
    if (showText) {
      const textEl = document.createElement("div");
      textEl.className = "card-text";
      textEl.textContent = phrase?.target?.text ?? "";
      body.appendChild(textEl);
    }
    card.appendChild(body);
    return card;
  }
}
